// Clients & brands management (advanced). Shown in navigation when the user
// can manage clients or already works with several of them.
import type { ColumnDef } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Archive, Building2, Pencil, Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api, call } from "@/api/client";
import { keys, useFrontDesk } from "@/api/hooks";
import type { Brand, Client } from "@/api/types";
import { formatDateTime } from "@/lib/format";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { ConfirmDialog } from "@/ui/dialog";
import { EmptyState, PageHeader, Section } from "@/ui/layout";
import { DataTable } from "@/components/data-table";
import { NameDialog } from "@/components/name-dialog";
import { campaignColumns } from "../campaigns/campaigns-page";
import { Breadcrumbs } from "../common/breadcrumbs";
import { notifyError } from "../common/notify";
import { PageError, PageLoading, useTitle, useRouteParams } from "../common/page";
import { useBrand, useBrands, useCampaigns, useClient, useClients } from "../common/queries";

const status = (archived: boolean) => (archived ? <Badge>Archivado</Badge> : <Badge tone="success">Activo</Badge>);

export function ClientsPage() {
  const { orgId } = useRouteParams("orgId");
  const queryClient = useQueryClient();
  const { data: fd } = useFrontDesk(orgId);
  const [showArchived, setShowArchived] = useState(false);
  const clients = useClients(orgId, showArchived);
  const [createOpen, setCreateOpen] = useState(false);
  useTitle("Clientes y marcas");
  const base = `/o/${orgId}`;
  const columns: Array<ColumnDef<Client>> = [
    {
      header: "Cliente",
      cell: ({ row }) => (
        <Link to={`${base}/clients/${row.original.id}`} className="font-medium hover:underline">
          {row.original.name}
        </Link>
      ),
    },
    { header: "Estado", cell: ({ row }) => status(row.original.archived) },
    { header: "Creado", cell: ({ row }) => formatDateTime(row.original.createdAt) },
  ];
  return (
    <div className="grid gap-6">
      <PageHeader
        title="Clientes y marcas"
        description="Organiza tus creatividades por cliente y marca. Útil si gestionas varias marcas o trabajas para terceros."
        actions={
          fd?.capabilities.manageClients ? (
            <Button
              variant="primary"
              onClick={() => {
                setCreateOpen(true);
              }}
            >
              <Plus /> Nuevo cliente
            </Button>
          ) : null
        }
      />
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={showArchived}
          onChange={(e) => {
            setShowArchived(e.target.checked);
          }}
        />{" "}
        Mostrar archivados
      </label>
      <DataTable
        label="Clientes"
        columns={columns}
        rows={clients.items}
        loading={clients.isLoading}
        hasMore={clients.hasNextPage}
        loadingMore={clients.isFetchingNextPage}
        onLoadMore={() => void clients.fetchNextPage()}
        empty={<EmptyState icon={<Building2 />} title="Aún no hay clientes" />}
      />
      <NameDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="Nuevo cliente"
        label="Nombre del cliente"
        submitLabel="Crear cliente"
        onSubmit={async (name) => {
          await call(() => api.POST("/orgs/{orgId}/clients", { params: { path: { orgId } }, body: { name } }));
          await queryClient.invalidateQueries({ queryKey: keys.org(orgId) });
        }}
      />
    </div>
  );
}

function useEntityActions(orgId: string) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const run = async (fn: () => Promise<unknown>, success: string) => {
    setPending(true);
    try {
      await fn();
      toast.success(success);
      await queryClient.invalidateQueries({ queryKey: keys.org(orgId) });
    } catch (problem) {
      notifyError(problem);
    } finally {
      setPending(false);
    }
  };
  return { pending, run, queryClient };
}

export function ClientDetailPage() {
  const { orgId, clientId } = useRouteParams("orgId", "clientId");
  const { data: fd } = useFrontDesk(orgId);
  const client = useClient(orgId, clientId);
  const brands = useBrands(orgId, clientId);
  const { pending, run, queryClient } = useEntityActions(orgId);
  const [renameOpen, setRenameOpen] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  useTitle(client.data?.name);
  if (client.isLoading) return <PageLoading />;
  if (client.error || !client.data) return <PageError error={client.error} orgId={orgId} />;
  const c = client.data;
  const base = `/o/${orgId}`;
  const manageClient = (fd?.capabilities.manageClients ?? false) && !c.archived;
  const manageBrands = (fd?.capabilities.manageBrands ?? false) && !c.archived;
  const path = { params: { path: { orgId, clientId } } };
  const columns: Array<ColumnDef<Brand>> = [
    {
      header: "Marca",
      cell: ({ row }) => (
        <Link to={`${base}/brands/${row.original.id}`} className="font-medium hover:underline">
          {row.original.name}
        </Link>
      ),
    },
    { header: "Estado", cell: ({ row }) => status(row.original.archived) },
  ];
  return (
    <div className="grid gap-6">
      <PageHeader
        eyebrow={
          <Link to={`${base}/clients`} className="hover:underline">
            Clientes y marcas
          </Link>
        }
        title={
          <span className="flex items-center gap-2">
            {c.name} {c.archived ? <Badge>Archivado</Badge> : null}
          </span>
        }
        actions={
          <>
            {manageBrands ? (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setBrandOpen(true);
                }}
              >
                <Plus /> Nueva marca
              </Button>
            ) : null}
            {manageClient ? (
              <>
                <Button
                  size="sm"
                  onClick={() => {
                    setRenameOpen(true);
                  }}
                >
                  <Pencil /> Renombrar
                </Button>
                <Button
                  size="sm"
                  variant="danger-outline"
                  onClick={() => {
                    setArchiveOpen(true);
                  }}
                >
                  <Archive /> Archivar
                </Button>
              </>
            ) : null}
          </>
        }
      />
      <Section title="Marcas">
        <DataTable
          label="Marcas"
          columns={columns}
          rows={brands.items}
          loading={brands.isLoading}
          hasMore={brands.hasNextPage}
          loadingMore={brands.isFetchingNextPage}
          onLoadMore={() => void brands.fetchNextPage()}
          empty={<EmptyState title="Sin marcas" />}
        />
      </Section>
      <NameDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        title="Renombrar cliente"
        label="Nombre"
        initial={c.name}
        submitLabel="Guardar"
        onSubmit={async (name) => {
          await call(() => api.PATCH("/orgs/{orgId}/clients/{clientId}", { ...path, body: { name } }));
          await queryClient.invalidateQueries({ queryKey: keys.org(orgId) });
        }}
      />
      <NameDialog
        open={brandOpen}
        onOpenChange={setBrandOpen}
        title="Nueva marca"
        label="Nombre de la marca"
        submitLabel="Crear marca"
        onSubmit={async (name) => {
          await call(() => api.POST("/orgs/{orgId}/clients/{clientId}/brands", { ...path, body: { name } }));
          await queryClient.invalidateQueries({ queryKey: keys.org(orgId) });
        }}
      />
      <ConfirmDialog
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        title="¿Archivar este cliente?"
        description="No podrás crear ni publicar nada nuevo bajo este cliente. Es definitivo; su historial se conserva."
        confirmLabel="Archivar"
        destructive
        pending={pending}
        onConfirm={() =>
          void run(
            () => call(() => api.POST("/orgs/{orgId}/clients/{clientId}/archive", path)),
            "Cliente archivado"
          ).then(() => {
            setArchiveOpen(false);
          })
        }
      />
    </div>
  );
}

export function BrandDetailPage() {
  const { orgId, brandId } = useRouteParams("orgId", "brandId");
  const { data: fd } = useFrontDesk(orgId);
  const brand = useBrand(orgId, brandId);
  const campaigns = useCampaigns(orgId, brandId, true);
  const { pending, run, queryClient } = useEntityActions(orgId);
  const [renameOpen, setRenameOpen] = useState(false);
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  useTitle(brand.data?.name);
  if (brand.isLoading) return <PageLoading />;
  if (brand.error || !brand.data) return <PageError error={brand.error} orgId={orgId} />;
  const b = brand.data;
  const base = `/o/${orgId}`;
  const manageBrand = (fd?.capabilities.manageBrands ?? false) && !b.archived;
  const manageCampaigns = (fd?.capabilities.manageCampaigns ?? false) && !b.archived;
  const path = { params: { path: { orgId, brandId } } };
  return (
    <div className="grid gap-6">
      <Breadcrumbs orgId={orgId} clientId={b.clientId} current={b.name} />
      <PageHeader
        eyebrow="Marca"
        title={
          <span className="flex items-center gap-2">
            {b.name} {b.archived ? <Badge>Archivada</Badge> : null}
          </span>
        }
        actions={
          <>
            {manageCampaigns ? (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setCampaignOpen(true);
                }}
              >
                <Plus /> Nueva campaña
              </Button>
            ) : null}
            {manageBrand ? (
              <>
                <Button
                  size="sm"
                  onClick={() => {
                    setRenameOpen(true);
                  }}
                >
                  <Pencil /> Renombrar
                </Button>
                <Button
                  size="sm"
                  variant="danger-outline"
                  onClick={() => {
                    setArchiveOpen(true);
                  }}
                >
                  <Archive /> Archivar
                </Button>
              </>
            ) : null}
          </>
        }
      />
      <Section title="Campañas">
        <DataTable
          label="Campañas"
          columns={campaignColumns(base)}
          rows={campaigns.items}
          loading={campaigns.isLoading}
          hasMore={campaigns.hasNextPage}
          loadingMore={campaigns.isFetchingNextPage}
          onLoadMore={() => void campaigns.fetchNextPage()}
          empty={<EmptyState title="Sin campañas" />}
        />
      </Section>
      <NameDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        title="Renombrar marca"
        label="Nombre"
        initial={b.name}
        submitLabel="Guardar"
        onSubmit={async (name) => {
          await call(() => api.PATCH("/orgs/{orgId}/brands/{brandId}", { ...path, body: { name } }));
          await queryClient.invalidateQueries({ queryKey: keys.org(orgId) });
        }}
      />
      <NameDialog
        open={campaignOpen}
        onOpenChange={setCampaignOpen}
        title="Nueva campaña"
        label="Nombre de la campaña"
        submitLabel="Crear campaña"
        onSubmit={async (name) => {
          await call(() => api.POST("/orgs/{orgId}/brands/{brandId}/campaigns", { ...path, body: { name } }));
          await queryClient.invalidateQueries({ queryKey: keys.org(orgId) });
        }}
      />
      <ConfirmDialog
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        title="¿Archivar esta marca?"
        description="No podrás crear ni publicar nada nuevo bajo esta marca. Es definitivo; su historial se conserva."
        confirmLabel="Archivar"
        destructive
        pending={pending}
        onConfirm={() =>
          void run(() => call(() => api.POST("/orgs/{orgId}/brands/{brandId}/archive", path)), "Marca archivada").then(
            () => {
              setArchiveOpen(false);
            }
          )
        }
      />
    </div>
  );
}
